import {
  CloseCircleFilled,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { Popconfirm, Tooltip, message } from "antd";
import type { TableColumnsType } from "antd";
import { productCategoryApi } from "api/product-category.api";
import { AppButton, AppTable, AppTableToolbar } from "components/antd-ui";
import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { ModalStatus } from "types/modal";
import { QueryParam } from "types/query";
import { ProductCategory } from "types/product-category";
import {
  ActionButton,
  TableLayout,
  TableLayoutFooter,
  getTableShowingRange,
} from "components/table";
import { getTitle } from "utils";
import { ProductCategoryModal } from "./components/ProductCategoryModal";

export const ProductCategoryPage = ({ title = "" }) => {
  const [searchValue, setSearchValue] = useState("");
  const [query, setQuery] = useState<QueryParam>({
    page: 1,
    limit: 10,
    search: "",
  });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ProductCategory[]>([]);
  const [total, setTotal] = useState(0);
  const [visibleModal, setVisibleModal] = useState(false);
  const [selectedProductCategory, setSelectedProductCategory] = useState<
    Partial<ProductCategory>
  >({});
  const [modalStatus, setModalStatus] = useState<ModalStatus>("create");

  const debounceSearch = useCallback(
    debounce(
      (keyword: string) =>
        setQuery((prev) => ({ ...prev, search: keyword, page: 1 })),
      300,
    ),
    [],
  );

  useEffect(() => {
    document.title = getTitle(title);
  }, []);

  useEffect(() => {
    fetchData();
  }, [query]);

  const fetchData = async () => {
    setLoading(true);
    const res = await productCategoryApi.findAll(query);
    setLoading(false);
    setData(res.data.productCategories);
    setTotal(res.data.total);
  };

  const handleDelete = async (row: ProductCategory) => {
    setLoading(true);
    try {
      await productCategoryApi.delete(row.id);
      message.success("Xóa thành công");
      fetchData();
    } finally {
      setLoading(false);
    }
  };

  const columns: TableColumnsType<ProductCategory> = [
    {
      title: "STT",
      key: "stt",
      width: 80,
      render: (_, __, index) => (
        <span>{(query.page - 1) * query.limit + index + 1}</span>
      ),
    },
    {
      title: "Tên danh mục",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Thao tác",
      key: "action",
      width: 120,
      align: "center",
      render: (_, record) => (
        <div className="flex gap-2 justify-center items-center">
          <Tooltip title="Cập nhật">
            <ActionButton
              aria-label="Cập nhật"
              onClick={() => {
                setSelectedProductCategory(record);
                setVisibleModal(true);
                setModalStatus("update");
              }}
            >
              <EditOutlined style={{ color: "#6c84e5" }} />
            </ActionButton>
          </Tooltip>
          <Tooltip title="Xóa">
            <Popconfirm
              onConfirm={() => handleDelete(record)}
              placement="top"
              title="Xác nhận xóa"
            >
              <ActionButton aria-label="Xóa">
                <DeleteOutlined style={{ color: "#ef4444" }} />
              </ActionButton>
            </Popconfirm>
          </Tooltip>
        </div>
      ),
    },
  ];

  const { showingFrom, showingTo } = getTableShowingRange(
    query.page,
    query.limit,
    total,
  );

  return (
    <div>
      <TableLayout
        filter={
          <AppTableToolbar
            search={{
              value: searchValue,
              placeholder: "Tìm kiếm",
              onChange: (value) => {
                setSearchValue(value);
                debounceSearch(value);
              },
              inputProps: {
                allowClear: {
                  clearIcon: (
                    <CloseCircleFilled
                      onClick={() => {
                        setSearchValue("");
                        debounceSearch.cancel();
                        setQuery((prev) => ({ ...prev, page: 1, search: "" }));
                      }}
                    />
                  ),
                },
              },
            }}
            leftActions={
              <AppButton
                tone="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setVisibleModal(true);
                  setModalStatus("create");
                  setSelectedProductCategory({});
                }}
              >
                Thêm mới
              </AppButton>
            }
            filterLabel={null}
          />
        }
        table={
          <AppTable<ProductCategory>
            rowKey="id"
            loading={loading}
            pagination={false}
            dataSource={data}
            columns={columns}
            scroll={{ x: 500 }}
          />
        }
        footer={
          <TableLayoutFooter
            showingFrom={showingFrom}
            showingTo={showingTo}
            total={total}
            currentPage={query.page}
            pageSize={query.limit}
            onChange={({ limit, page }) => {
              query.page = page;
              query.limit = limit;
              setQuery({ ...query });
            }}
          />
        }
      />

      <ProductCategoryModal
        onSubmitOk={fetchData}
        onClose={() => setVisibleModal(false)}
        visible={visibleModal}
        productCategory={selectedProductCategory}
        status={modalStatus}
      />
    </div>
  );
};
